import _ from "lodash";

import ExternalLogger from "../logger/ExternalLogger";

const AdviceService = function (
    $modal,
    $q,
    $rootScope,
    $state,
    $timeout,
    $window,
    accountService,
    adviceFactory,
    FooterFactory,
    investmentOverviewFactory,
    paeService,
    PrimaryPlanTxnAccessService,
    TransactionAccessRetrService
) {
    /**
     * Logger.
     */
    const logger = ExternalLogger.getInstance("AdviceService");

    const service = this;
    service.dataLoaded = false;
    service.nextGenAdvisoryServicesEnabled = false;
    service.fundLineupStatus = "";
    service.ibbotsonEnabled = false; //Ibbotson? Formerly advisoryServiceEnabled
    service.feEnabled = false; //FE? Formerly financialEngineSSOEnabled
    service.enhancedNextGenAdvisoryServicesEnabled = false;
    service.enrolledMAFe = false;
    service.quickEnrollMAFeEligible = false;
    service.quickEnrollMAMsEligible = false;
    service.autoEnrollMAFeEligible = false;
    service.autoEnrollMAMsEligible = false;
    service.inQEFlow = false;

    service.managedAccountsOptions = {};
    service.advisoryServicesOptions = {};
    service.individualOptions = {};
    service.moneyTypeGroupings = {};
    service.MAAllocationsOnEnrollment = {};

    service.managedAccountsAllowed = false;
    service.adviceAllowed = false;
    service.guidanceAllowed = false; //true for Ibbotson for some participants

    service.alreadyEnrolled = false;
    service.isRebalancerAllowed = false;
    service.partHasZeroBalance = false;
    service.noEquityMixData = false;

    service.guidanceHeaderDataLoaded = false;

    service.lambdaURL = null;

    let txnAccess_WNAMAS = false;
    let txnAccess_WNFESS = false;

    const throttledGetOverviewBasic = _.throttle(investmentOverviewFactory.getOverviewBasic, 1000, {
        trailing: false
    });

    /**
     *  Hash of possible fund lineup status values
     * @type {Object}
     */
    service.FUND_LINEUP_STATUS = {
        PENDING: "pending",
        INELIGIBLE: "ineligible",
        FAILED: "failed",
        ACTIVE: "active",
        NO_LINEUP: "no_lineup"
    };

    /* handling dynamic nature of MA service provider names */
    service.ManagedAccountsTranslations = {};

    /**
     * Indicates if the ppt is Fe and eligible for MA
     */
    service.isMAFeEligible = function () {
        return (
            service.feEnabled &&
            _.get(
                service.managedAccountsOptions,
                "groupServiceRules.allowFullManagedAccounts",
                null
            )
        );
    };

    service.init = async function (isLiatFlow, usePrimaryPlanContext) {
        const method = "init()";
        logger.debug("{0}- Input param: isLiatFlow={1}, usePrimaryPlanContext={2}", [
            method,
            isLiatFlow,
            usePrimaryPlanContext
        ]);

        // this is called second
        const getAccountOverview = async function (isLiatFlow, groupIdParam, individualIdParam) {
            //advice is allowed if either type of advice is enabled..but NOT if both are enabled (only one is allowed)
            const overviewMethod = isLiatFlow ? "getLiatOverview" : "getOverview";

            logger.debug(
                "{0}.getAccountOverview()- About to call InvestmentOverviewFactory with gaId={1},indId={2}, isLiatFlow={3}, overviewMethod={4}",
                [method, groupIdParam, individualIdParam, isLiatFlow, overviewMethod]
            );

            const data = await _.get(
                investmentOverviewFactory,
                overviewMethod
            )({
                individualId: individualIdParam,
                groupId: groupIdParam
            });
            if (data?.status === 200) {
                logger.debug(
                    "{0}.getAccountOverview()-  Got result back from investmentOverviewFactory. Setting attributes and dataLoaded to true. Resolve.",
                    [method]
                );
                try {
                    const popDataResp = await populateInvestmentOverviewData(data.data);
                    if (popDataResp) {
                        service.dataLoaded = true;
                        const tmp = { ...data.data, dataLoaded: true };
                        return tmp;
                    }
                } catch (error) {
                    console.log("Error with investment overview service", error);
                }
            }
            const tmp = { ...data.data, dataLoaded: true };
            return tmp;
        };

        // this is called first
        try {
            const txnAccessResp = await checkTxnAccessManagedAccount(
                isLiatFlow,
                usePrimaryPlanContext
            );
            if (txnAccessResp) {
                const { groupId, individualId } = accountService.getActiveGroupAccount();

                logger.debug(
                    "{0}- Use Active plan context. Calling getAccountOverview with active plan, gaId={1} ",
                    [method, groupId]
                );

                if (groupId && individualId) {
                    const overviewResp = await getAccountOverview(
                        isLiatFlow,
                        groupId,
                        individualId
                    );

                    return overviewResp;
                }
            }
        } catch (error) {
            console.error("Error with transaction access retrieval service", error);
        }
    };

    async function checkTxnAccessManagedAccount(isLiatFlow, usePrimaryPlanContext) {
        const deferred = $q.defer();

        const processTxnResult = function (data) {
            txnAccess_WNAMAS = data.WNAMAS ? data.WNAMAS !== "false" : false;
            txnAccess_WNFESS = data.WNFESS ? data.WNFESS !== "false" : false;
        };

        // reset
        txnAccess_WNAMAS = false;
        txnAccess_WNFESS = false;

        // Query for Primary Group
        if (isLiatFlow || usePrimaryPlanContext) {
            const txnCodeParams = {
                txnCode: "WNAMAS,WNFESS"
            };
            PrimaryPlanTxnAccessService.query(txnCodeParams)
                .then(function (data) {
                    processTxnResult(data);
                    deferred.resolve(data);
                })
                .catch(function (err) {
                    logger.debug(
                        "AdviceService.checkTxnAccessManagedAccount()- Call failed PrimaryPlanTxnAccessService.query()"
                    );
                    deferred.reject();
                });
        }
        // Query for specific group
        else {
            const { groupId, individualId } = accountService.getActiveGroupAccount();
            if (groupId && individualId) {
                const txnCodeParamWithGroup = {
                    txnCode: "WNAMAS,WNFESS",
                    individualId: individualId,
                    groupId: groupId
                };
                await TransactionAccessRetrService.queryWithAccount(
                    txnCodeParamWithGroup,
                    function (data) {
                        processTxnResult(data);
                        deferred.resolve(data);
                    },
                    function (error) {
                        logger.debug(
                            "AdviceService.checkTxnAccessManagedAccount()- Call failed transactionAccessFactor.queryWithAccount()"
                        );
                        deferred.reject();
                    }
                );
            }
        }

        return deferred.promise;
    }

    function propertyToBoolean(val) {
        return val === "true";
    }

    function filterSamlData(samlResponse) {
        return (samlResponse.replace ? samlResponse : "").replace(/\\n/g, "\n");
    }

    function getFeSamlToken(account, pagecode, treatment, lastAlert) {
        const deferred = $q.defer();
        let getFeSamlTokenParams = {};
        let tokenFunction = adviceFactory.getFeSamlToken;

        if (account && account.individualId) {
            getFeSamlTokenParams = {
                individualId: account.individualId,
                groupId: account.groupId
            };
            if (pagecode || treatment || lastAlert) {
                getFeSamlTokenParams.pagecode = pagecode;
                getFeSamlTokenParams.treatment = treatment;
                getFeSamlTokenParams.lastAlert = lastAlert;
                getFeSamlTokenParams.clickthrough = true;
            }
            tokenFunction = adviceFactory.getFeSamlTokenForPlan;
        }

        tokenFunction.query(
            getFeSamlTokenParams,
            function (data) {
                deferred.resolve(data);
            },
            function (response) {
                //$state.go('error');
                deferred.reject();
            }
        );
        return deferred.promise;
    }

    const getFeSamlTokenResult = function (data) {
        const form = document.createElement("form");
        const textarea = document.createElement("textarea");
        Object.assign(form, {
            id: "SAMLFORM",
            method: "post",
            target: "_blank"
        });
        textarea.setAttribute("id", "SAMLResponse");
        textarea.setAttribute("name", "SAMLResponse");
        textarea.setAttribute("type", "hidden");
        const deferred = $q.defer();
        logger.info("getFeSamlTokenResult()");
        document.querySelector("body").querySelector("#SAMLFORM")?.remove();
        document.querySelector("body").append(form);
        document.querySelector("#SAMLFORM").append(textarea);
        document.querySelector("#SAMLResponse").innerHTML = filterSamlData(data.samlResponse);
        document.querySelector("#SAMLFORM").setAttribute("action", data.postUrl);
        const feWindow = window.open("", "feWindow");
        document.querySelector("#SAMLFORM").setAttribute("target", "feWindow");
        if (!feWindow || feWindow.closed || typeof feWindow.closed == "undefined") {
            alert("Please disable your pop up blocker to view your managed accounts.");
            deferred.reject();
        }
        let feWindowLoaded = false;

        if (feWindow?.document.readyState === "complete") {
            document.querySelector(`#SAMLFORM`).submit();
            feWindowLoaded = true;
            deferred.resolve();
        } else
            feWindow.addEventListener("load", function () {
                document.querySelector("#SAMLFORM").submit();
                feWindowLoaded = true;
                deferred.resolve();
            });

        $timeout(function () {
            if (!feWindowLoaded) {
                let errorMessage = "There was an error launching managed accounts.";
                if (!feWindow || feWindow.closed || typeof feWindow.closed == "undefined") {
                    errorMessage +=
                        " Please disable your pop up blocker to view your managed accounts.";
                }
                alert(errorMessage);
                deferred.reject();
            }
        }, 6000);

        return deferred.promise;
    };

    service.navigateFE = function () {
        const deferred = $q.defer();
        logger.info("navigateFE()");

        const getFeSamlTokenFault = function () {
            logger.info("getFeSamlTokenFault()");
        };

        getFeSamlToken(accountService.getActiveGroupAccount())
            .then(getFeSamlTokenResult)
            .then(deferred.resolve)
            .catch(getFeSamlTokenFault);

        return deferred.promise;
    };

    service.navigateFESSO = function (pageCode, treatment, viewCode) {
        const deferred = $q.defer();
        logger.info("navigateFESSO()");

        const navigateFESSO = function () {
            logger.info("navigateFESSO()");
        };

        getFeSamlToken(accountService.getActiveGroupAccount(), pageCode, treatment, viewCode)
            .then(getFeSamlTokenResult)
            .then(deferred.resolve)
            .catch(navigateFESSO);

        return deferred.promise;
    };

    function getTasSSOURL(account) {
        const deferred = $q.defer();
        const serviceURLFunction = adviceFactory.getTasSSOServiceUrl;

        const getTasSSOParams = {
            individualId: account.individualId,
            groupId: account.groupId
        };

        serviceURLFunction.query(
            getTasSSOParams,
            function (data) {
                deferred.resolve(data);
            },
            function (response) {
                //$state.go('error');
                deferred.reject();
            }
        );
        return deferred.promise;
    }

    //JIRA: https://jira.retirementpartner.com/browse/PW-29074
    //JIRA: https://jira.retirementpartner.com/browse/PW-29102
    /*
       To support cross-platform 'special characters', the following values need to be replaced to display as expected
       REF: https://www.fileformat.info/info/unicode/char/a.htm
       REF: https://www.w3schools.com/charsets/ref_html_utf8.asp
       AltCodes Reference:  https://www.i18nqa.com/debug/utf8-debug.html
    */
    const markupTranslations = {
        descr: "This is to provide a logical mapping for special characters used in MA provider names.",
        data: [
            { regex: /<sup>TM<\/sup>/i, replacement: "™", description: "trademark symbol" },
            { regex: /<TM>/i, replacement: "™", description: "trademark symbol" },
            { regex: /<sup>SM<\/sup>/i, replacement: "℠", description: "service mark symbol" },
            { regex: /<SM>/i, replacement: "℠", description: "service mark symbol" },
            { regex: /<R>/i, replacement: "®", description: "registered symbol" },
            { regex: /<C>/i, replacement: "©", description: "copyright symbol" }
        ]
    };

    /**
     * transform the <sup>SM<sup> and <sup>TM</sup> html tag to plain text
     * add other html tag if needed
     * @param {string} value
     * @returns {string}
     */
    function superiors(value) {
        const method = "superiors(" + value + ")";
        let rc = value ? value : null;

        if (rc && rc.indexOf("<") != -1 && rc.indexOf(">") != -1) {
            for (let i = 0; i < markupTranslations.data.length; i++) {
                const m = markupTranslations.data[i];
                if (rc.search(m.regex) > -1) {
                    rc = rc.replace(m.regex, m.replacement);
                    logger.debug("{0} - replaced pattern=[{1}], regex=[{2}], replacement=[{3}]", [
                        method,
                        m.pattern,
                        m.regex,
                        m.replacement
                    ]);
                }
            }
        }

        logger.debug("{0} - returning : {1}", [method, rc]);
        return rc;
    }

    function populateInvestmentOverviewData(data) {
        const deferred = $q.defer();

        if (data) {
            service.managedAccountsOptions = data.managedAccountsOptions;
            service.advisoryServicesOptions = data.advisoryServicesOptions;
            service.individualOptions = data.individualOptions;
            service.moneyTypeGroupings = data.moneyTypeGroupings;
            service.nextGenAdvisoryServicesEnabled =
                _.get(
                    service.managedAccountsOptions,
                    "groupServiceRules.allowNextGenAdvisoryServices",
                    null
                ) &&
                service.ibbotsonEnabled &&
                !service.feEnabled;
            service.enhancedNextGenAdvisoryServicesEnabled = _.get(
                service.managedAccountsOptions,
                "groupServiceRules.allowEnhancedNextGenAdvisoryServices",
                null
            );
            service.fundLineupStatus = _.get(service.managedAccountsOptions, "fundLineupStatus");

            if (
                _.isUndefined(service.fundLineupStatus) ||
                _.isNull(service.fundLineupStatus) ||
                _.isEmpty(service.fundLineupStatus)
            ) {
                service.fundLineupStatus = service.FUND_LINEUP_STATUS.NO_LINEUP;
            }

            service.managedAccountsAllowed =
                (service.ibbotsonEnabled || service.feEnabled) &&
                !(service.ibbotsonEnabled && service.feEnabled) &&
                _.get(
                    service.managedAccountsOptions,
                    "groupServiceRules.allowFullManagedAccounts",
                    null
                );

            service.adviceAllowed = _.get(
                service.managedAccountsOptions,
                "groupServiceRules.allowAdvice",
                null
            );
            service.guidanceAllowed = _.get(
                service.managedAccountsOptions,
                "groupServiceRules.allowGuidance",
                null
            );
            service.alreadyEnrolled = _.get(
                service.managedAccountsOptions,
                "enrolledInFullManagedAccounts",
                null
            );
            service.isRebalancerAllowed = data.isRebalancerAllowed;
            service.partHasZeroBalance = data.partHasZeroBalance;
            service.noEquityMixData = data.noEquityMixData;

            if (
                service.managedAccountsAllowed ||
                service.adviceAllowed ||
                service.guidanceAllowed
            ) {
                /* handling dynamic nature of MA service provider names */
                angular.extend(service.ManagedAccountsTranslations, {
                    advisoryServicesProgramName: superiors(
                        _.get(service.managedAccountsOptions, "advisoryServicesProgramName", "")
                    ),
                    managedAccountsProgramName: superiors(
                        _.get(service.managedAccountsOptions, "managedAccountsProgramName", "")
                    ),
                    onlineAdviceProgramName: superiors(
                        _.get(service.managedAccountsOptions, "onlineAdviceProgramName", "")
                    ),
                    onlineGuidanceProgramName: superiors(
                        _.get(service.managedAccountsOptions, "onlineGuidanceProgramName", "")
                    ),
                    termsOfServiceUrl: superiors(
                        baseApiUrl + _.get(service.managedAccountsOptions, "termsOfServiceUrl", "")
                    ),
                    privacyUrl: superiors(_.get(service.managedAccountsOptions, "privacyUrl", "")),
                    disclosureUrl: superiors(
                        _.get(service.managedAccountsOptions, "disclosureUrl", "")
                    ),
                    disclosureText: superiors(
                        _.get(service.managedAccountsOptions, "disclosureText", "")
                    )
                });
            }

            PrimaryPlanTxnAccessService.query({ txnCode: "WNAMAS,WNFESS" }).then(
                (txnResponse) => {
                    service.ibbotsonEnabled = txnResponse.WNAMAS;
                    service.feEnabled = txnResponse.WNFESS;
                    deferred.resolve(true);
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        }
        return deferred.promise;
    }

    service.getFeEnrollmentData = function () {
        logger.debug("entering getFeEnrollmentData()");
        const account = accountService.getActiveGroupAccount();
        logger.debug("ActiveGroupAccount: {0}", [account]);
        const accountDetails = accountService.getAccountDetails();
        const deferred = $q.defer();
        const serviceURL = adviceFactory.getMAEnrollmentData;
        const whatItCostsURL = adviceFactory.getWhatItCostsData;
        const getMStarPlanDetailsURL = adviceFactory.getMStarPlanDetails;
        const params = {
            groupId: account.groupId
        };
        const deferred1 = $q.defer();
        logger.debug("calling service. url={0} params={1}", [
            "getMAEnrollmentData",
            JSON.stringify(params)
        ]);
        serviceURL.query(
            params,
            function (data) {
                logger.debug("service success. url={0} response={1}", [
                    "getMAEnrollmentData",
                    JSON.stringify(data)
                ]);
                deferred1.resolve(data);
            },
            function (response) {
                logger.debug("service ERROR. url={0} response={1}", [
                    "getMAEnrollmentData",
                    JSON.stringify(response)
                ]);
                deferred1.reject();
            }
        );

        logger.debug("calling service. url={0} params= {1}", [
            "getWhatItCostsData",
            JSON.stringify(params)
        ]);
        const deferred2 = $q.defer();
        whatItCostsURL.query(
            params,
            function (data) {
                logger.debug("service success. url={0} response={1}", [
                    "getWhatItCostsData",
                    JSON.stringify(data)
                ]);
                deferred2.resolve(data);
            },
            function (response) {
                logger.debug("service ERROR. url={0} response={1}", [
                    "getWhatItCostsData",
                    JSON.stringify(response)
                ]);
                deferred2.reject();
            }
        );

        const deferred3 = $q.defer();
        //Calling this service for FE plan as well since we want to display company stock message(s) per PART-32245
        if (service.ibbotsonEnabled || service.feEnabled) {
            getMStarPlanDetailsURL.query(
                function (data) {
                    logger.debug("service success. url={0} response={1}", [
                        "getMStarPlanDetailsURL",
                        JSON.stringify(data)
                    ]);
                    deferred3.resolve(data);
                },
                function (response) {
                    logger.debug("service ERROR. url={0} response={1}", [
                        "getMStarPlanDetailsURL",
                        JSON.stringify(response)
                    ]);
                    deferred3.reject();
                }
            );
        } else {
            deferred3.resolve([]);
        }

        const promiseA = deferred1.promise;
        const promiseB = deferred2.promise;
        const promiseC = deferred3.promise;

        $q.all([promiseA, promiseB, promiseC]).then(
            function (results) {
                //format response
                const maData = results[0];
                const feeData = results[1];
                const mstarPlanData = results[2];
                const feEnrollmentData = {
                    dateOfBirth: maData.dateOfBirth,
                    email: maData.email,
                    firstName: maData.firstName,
                    gender: maData.gender,
                    lastName: maData.lastName,
                    phoneNumber: maData.phoneNumber,
                    salary: maData.salary,
                    maSalary: maData.maSalary,
                    state: maData.state,
                    planName: accountDetails.name,
                    maDateOfBirth: maData.maDateOfBirth,
                    maDobSource: maData.maDobSource,
                    maGender: maData.maGender,
                    maGenderSource: maData.maGenderSource,
                    managedAccountsManagementFees: feeData.managedAccountsManagementFees,
                    mstarPlanData: mstarPlanData
                };
                deferred.resolve(feEnrollmentData);
            },
            function (errorMsg) {
                deferred.reject();
            }
        );
        return deferred.promise;
    };

    service.saveMAEnrollmentData = function (
        emailId,
        phoneNumber,
        alreadyEnrolled,
        selectedPlanList,
        salary,
        dob,
        dobSource,
        gender,
        genderSource
    ) {
        const account = accountService.getActiveGroupAccount();
        const accountDetails = accountService.getAccountDetails();
        const deferred = $q.defer();

        if (!selectedPlanList || (selectedPlanList && selectedPlanList.length == 0)) {
            selectedPlanList = [];
            selectedPlanList.push(account.groupId);
        }

        const data = {
            groupId: account.groupId,
            enrollment: {
                email: emailId,
                phoneNumber: phoneNumber,
                salary: salary,
                alreadyEnrolledInManagedAccounts: alreadyEnrolled,
                gaIDList: selectedPlanList,
                birthDate: dob,
                birthDateSource: dobSource,
                gender: gender,
                genderSource: genderSource
            }
        };

        adviceFactory.saveMAEnrollmentData(data).then(
            function success(response) {
                service.MAAllocationsOnEnrollment.investments = response.managedAccountsAllocations;
                service.MAAllocationsOnEnrollment.planName = accountDetails.name;

                // For M*, a participant can enroll in multiple plans. This endpoint will
                // respond with a list of plans the participant is enrolled in for M*.
                // We will store this separately so it does not impact the existing FE logic.
                service.MAAllocationsOnEnrollment.morningstarPlans = [];

                if (response.maEnrollmentSaveDetails) {
                    // Each plan will be a key/value on the maEnrollmentSaveDetails object
                    _.forOwn(response.maEnrollmentSaveDetails, function (value, key) {
                        service.MAAllocationsOnEnrollment.morningstarPlans.push({
                            planName: value.planName,
                            investments: value.managedAccountsAllocations,
                            groupId: key,
                            errorDetails: value.errorDetails
                        });
                    });
                }

                deferred.resolve();
            },
            function error(response) {
                deferred.reject();
            }
        );
        return deferred.promise;
    };

    service.getMAAllocationsOnEnrollment = function () {
        return service.MAAllocationsOnEnrollment;
    };

    service.invalidSetupForEnhancedNextGenAdvisoryServicesSetup = function () {
        console.log(
            "Invalid setup - CXRDW is turned on (true), but service rule allowEnhancedNextGenAdvisoryServices is not turned on (false)"
        );
        $state.go("error");
    };

    service.validSetupForEnhancedNextGenAdvisoryServicesSetup = function () {
        $window.location.href = "/participant/home/#/managed-accounts";
    };

    service.navigateIbbotson = function (showReturnModal) {
        showReturnModal = _.isUndefined(showReturnModal) ? true : showReturnModal;
        const windowName = "ibbotsonTab";

        logger.info("navigateIbbotson()");

        const getTasSSOResult = function (data) {
            logger.debug("getTasSSOResult()");

            if (
                service.nextGenAdvisoryServicesEnabled &&
                service.managedAccountsOptions.enrolledInFullManagedAccounts
            ) {
                const groupAccount = accountService.getAccountDetails();
                sessionStorage.setItem("gId", groupAccount.groupId);
                sessionStorage.setItem("indId", groupAccount.individualId);

                if (!service.enhancedNextGenAdvisoryServicesEnabled) {
                    //show error page - invalid setup
                    service.invalidSetupForEnhancedNextGenAdvisoryServicesSetup();
                } else if (service.enhancedNextGenAdvisoryServicesEnabled) {
                    //only do this when both CXRDW KTMG option is turned on (true) and when the ga_service rule for
                    service.validSetupForEnhancedNextGenAdvisoryServicesSetup();
                } else {
                    $window.location.href = "/participant/home/#/max-dashboard";
                }
            } else if (!service.enhancedNextGenAdvisoryServicesEnabled) {
                //show error page - invalid setup
                service.invalidSetupForEnhancedNextGenAdvisoryServicesSetup();
            } else if (service.enhancedNextGenAdvisoryServicesEnabled) {
                //only do this when both CXRDW KTMG option is turned on (true) and when the ga_service rule for
                service.validSetupForEnhancedNextGenAdvisoryServicesSetup();
            } else if (!service.ibbotsonWindowId) {
                service.ibbotsonWindowId = window.open("about:blank", windowName);
                if (
                    !service.ibbotsonWindowId ||
                    service.ibbotsonWindowId.closed ||
                    typeof service.ibbotsonWindowId.closed === "undefined"
                ) {
                    logger.debug(
                        "Cannot open window to SSO to Ibbotson. Possibly due to popup blocker."
                    );
                    alert("Please disable your pop up blocker to view your managed accounts.");
                } else {
                    service.ibbotsonWindowId.location.href = data.serviceURL;

                    if (showReturnModal) {
                        $modal.open({
                            template: "<advisory-services-ibbotson></advisory-services-ibbotson>"
                        });
                    }
                }
                service.ibbotsonWindowId = null;
            }
        };

        const getTasSSOURLFault = function () {
            logger.info("getTasSSOURLFault()");
        };

        const isParticipantApp = function () {
            return $window.location.href.indexOf("/participant/accounts") > -1;
        };

        const paeCheckSuccess = function (response) {
            let allowNavigation = false;
            //console.log('paeCheck response: '+response ? JSON.stringify(response) : 'null');
            if (response) {
                if (response.partnerSSOResponse === false) {
                    allowNavigation = true;
                }
            } else {
                console.log("unable to verify pae user status", response);
            }

            if (allowNavigation) {
                return getTasSSOURL(accountService.getActiveGroupAccount())
                    .then(getTasSSOResult)
                    .catch(getTasSSOURLFault);
            } else {
                console.log("pae users are prohibited from max-dashboard for enrolled ppts");
                return $rootScope.participantAppURL + "/#/guidance/allocations/selection";
            }
        };

        const paeCheckFault = function (response) {
            console.log("failed to perform pae user check.", response);
        };

        //determine if the ppt is a pae user...
        return paeService.checkPaeUser().then(paeCheckSuccess, paeCheckFault);
    };

    service.navigateAdvice = function () {
        $state.go("advice");
    };

    service.navigateAllocationSelection = function () {
        accountService.setPrimaryGroupAccount().then(function () {
            const primaryPlan = accountService.getPrimaryGroupAccount();
            $state.go("allocations.selection");
        });
    };

    service.getLegalDocs = function () {
        logger.debug("entering getLegalDocs..");
        const groupAccount = accountService.getActiveGroupAccount();

        const deferred = $q.defer();
        const serviceURL = adviceFactory.getLegalDocsUrl;
        const params = {
            groupId: groupAccount.groupId
        };
        logger.debug("getLegalDocs - params=[{0}]", [params]);
        serviceURL.query(
            params,
            function (data) {
                logger.debug("getLegalDocs response : {0}", [data]);
                deferred.resolve(data.managedAccountsLegalDocs);
            },
            function (response) {
                logger.debug("getLegalDocs()- Call failed");
                deferred.reject();
            }
        );
        return deferred.promise;
    };

    service.getKeyTalkNumber = function () {
        const groupAccount = accountService.getActiveGroupAccount();
        const deferred = $q.defer();
        FooterFactory.query(
            { gaId: groupAccount.groupId },
            function (data) {
                deferred.resolve(data.phone);
            },
            function (error) {
                logger.debug("getKeyTalkNumber()- Call failed");
                deferred.resolve("");
            }
        );
        return deferred.promise;
    };

    /**
     * Gathers the data for the what it costs view for
     * custom enrollment
     *
     * @returns {*|promise}
     */
    service.getWhatItCostsData = function () {
        logger.debug("entering getWhatItCostsData..");

        const deferred = $q.defer();

        const groupAccount = accountService.getActiveGroupAccount();
        logger.debug("getWhatItCostsData({0})", [groupAccount.groupId]);
        const params = {
            groupId: groupAccount.groupId
        };

        const whatItCostsURL = adviceFactory.getWhatItCostsData;

        whatItCostsURL.query(
            params,
            function (data) {
                deferred.resolve(data);
            },
            function (response) {
                deferred.reject();
            }
        );

        return deferred.promise;
    };

    /**
     * Retrieve Investement overview basic for Guidance header link. Always use Primary Group ID
     */
    service.getGuidanceHeaderInfo = function () {
        const method = "getGuidanceHeaderInfo()";
        const deferred = $q.defer();

        // calling service -- always using Primary GA_ID
        //----------------------
        logger.debug(
            "{0} - In method. About to call investmentOverviewFactory.getOverviewBasic() ",
            [method]
        );

        throttledGetOverviewBasic({
            individualId: accountService.getPrimaryGroupAccount().individualId,
            groupId: accountService.getPrimaryGroupAccount().groupId
        })
            .success(async function (data) {
                logger.debug("{0} - successful got data from getOverviewBasic()", [method]);

                await populateInvestmentOverviewData(data);

                service.guidanceHeaderDataLoaded = true;

                deferred.resolve(data);
            })
            .error(function (err) {
                logger.debug("{0} - getOverviewBasic() failed", [method]);
                deferred.reject(err);
            });

        return deferred.promise;
    };

    /**
     * Service to get the saml token.
     */
    service.getToken = function () {
        logger.debug("entering getToken..");

        const deferred = $q.defer();

        adviceFactory.getToken().then(
            function success(response) {
                deferred.resolve(response);
            },
            function error(fault) {
                deferred.reject(fault);
            }
        );

        return deferred.promise;
    };

    /**
     * Service to get Advice Dashboard base URI for the call
     */
    service.getMTRLambdaBaseURI = function () {
        logger.debug("entering getMTRLambdaBaseURI..");

        const deferred = $q.defer();

        adviceFactory.getMTRLambdaBaseURI().then(
            function success(response) {
                deferred.resolve(response);
            },
            function error(fault) {
                deferred.reject(fault);
            }
        );

        return deferred.promise;
    };

    /**
     * Service to get the Max ID.
     */
    service.getMaxID = function (samlToken) {
        logger.debug("entering getMaxID..");

        const deferred = $q.defer();

        adviceFactory.getMaxID(samlToken, service.lambdaURL).then(
            function success(response) {
                deferred.resolve(response);
            },
            function error(fault) {
                deferred.reject(fault);
            }
        );

        return deferred.promise;
    };

    /**
     * Service to save the one-time online
     * enrollment into advice.
     */
    service.saveAdviceEnroll = function (payload) {
        logger.debug("entering saveAdviceEnroll..");

        const deferred = $q.defer();

        adviceFactory.saveAdviceEnroll(payload, service.lambdaURL).then(
            function success(response) {
                deferred.resolve(response);
            },
            function error(fault) {
                deferred.reject(fault);
            }
        );

        return deferred.promise;
    };
};

AdviceService.$inject = [
    "$modal",
    "$q",
    "$rootScope",
    "$state",
    "$timeout",
    "$window",
    "accountService",
    "adviceFactory",
    "FooterFactory",
    "investmentOverviewFactory",
    "paeService",
    "PrimaryPlanTxnAccessService",
    "TransactionAccessRetrService"
];
export default AdviceService;
